/**
 * The signed-in owner's referral standing: the code they typed at signup, and
 * the code they can hand to other owners.
 *
 * One RPC (`my_owner_referral`, 20261012085002) rather than two table reads,
 * because the referrer's NAME is the one thing the portal prints that an owner
 * may not read from `profiles` directly. The function is SECURITY DEFINER and
 * resolves it for the caller only.
 *
 * Nothing here grants anything. A claim sits at `pending` until a reward step
 * — still an open product decision — advances it, so this hook reports a
 * status and never computes an entitlement.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type OwnerReferralStatus =
  | "pending"
  | "approved"
  | "rewarded"
  | "rejected";

export type OwnerReferral = {
  /** The owner's own code, once CoLiviq has issued one. */
  ownCode: string | null;
  /** The code they typed at signup, exactly as recorded. */
  claimedCode: string | null;
  claimStatus: OwnerReferralStatus | null;
  /**
   * Whether that code matched a real owner. Distinct from a missing name:
   * a referrer with a blank profile is still a referrer, and the portal must
   * not report a good code as unrecognised.
   */
  referrerKnown: boolean;
  referrerName: string | null;
};

const EMPTY: OwnerReferral = {
  ownCode: null,
  claimedCode: null,
  claimStatus: null,
  referrerKnown: false,
  referrerName: null,
};

/** The migration is unmerged on preview databases; degrade, do not throw. */
function isMissingFunction(error: {
  code?: string;
  message?: string;
}): boolean {
  if (error?.code === "PGRST202" || error?.code === "42883") return true;
  return /could not find the function|does not exist/i.test(
    error?.message ?? ""
  );
}

type Row = {
  own_code: string | null;
  claimed_code: string | null;
  claim_status: string | null;
  referrer_known: boolean | null;
  referrer_name: string | null;
};

export function useOwnerReferral(enabled = true) {
  const { user } = useAuth();

  return useQuery<OwnerReferral | null>({
    queryKey: ["owner-referral", user?.id ?? null],
    enabled: enabled && Boolean(user?.id),
    queryFn: async () => {
      const { data, error } = await (
        supabase as unknown as {
          rpc: (fn: string) => Promise<{
            data: unknown;
            error: { code?: string; message?: string } | null;
          }>;
        }
      ).rpc("my_owner_referral");

      if (error) {
        if (isMissingFunction(error)) return null;
        throw error;
      }

      // A set-returning function comes back as an array of one.
      const row = (Array.isArray(data) ? data[0] : data) as Row | undefined;
      if (!row) return EMPTY;

      return {
        ownCode: row.own_code ?? null,
        claimedCode: row.claimed_code ?? null,
        claimStatus: (row.claim_status as OwnerReferralStatus) ?? null,
        referrerKnown: row.referrer_known === true,
        referrerName: row.referrer_name?.trim() ? row.referrer_name : null,
      };
    },
  });
}
